import {
    AppAttributionSettings,
    AppStoreInfo,
    CreateAppAttributes,
    EditAppAttributes,
    Interstitial,
    TestMode,
    UnifiedApp,
} from 'app/features/apps/interfaces/app.interface';
import {MediationEngine, Platform} from '../../../../globalTypes';

export class AppModel implements CreateAppAttributes, EditAppAttributes {
    id: string;
    name: string;
    bundleId: string;
    ownerId: string;
    platform: Platform;
    filterMatureContent: boolean;
    coppa: boolean;
    enableStackCmp: boolean;
    disableNetworksAutoconnection: boolean;
    mediationEngine?: MediationEngine;

    readonly applicationKey: string;
    readonly storeUrl: string;
    interstitialMode: Interstitial;
    disableAdmobSync: boolean;
    s2sRewardCallbackUrl: string;
    s2sRewardCallbackKey: string;
    googlePlayConsoleCredentials: string;
    testMode: TestMode;
    testDeviceIds: Array<string>;
    revenueShares: Array<{email: string; percent: number}>;
    permissions: {
        canUpdateBundleId: boolean;
        canViewAdNetworkMonitor: boolean;
        canViewLineItems: boolean;
        canViewConfiguration: boolean;
        canUseAdmobEmergencyAlgorithm?: boolean;
        canViewMergedInterstitials: boolean;
        canViewLegacyAdTypes: boolean;
    };
    attributionSettings: AppAttributionSettings;
    unifiedApp?: UnifiedApp;
    appStoreInfo?: AppStoreInfo;
    yourAppUrl?: string;
    iconUrl?: string;

    static getDefaultModel(ownerId: string): CreateAppAttributes {
        return {
            name: '',
            bundleId: '',
            ownerId,
            platform: null,
            filterMatureContent: false,
            coppa: false,
            disableNetworksAutoconnection: false,
        };
    }

    static getCreateAppDto(formValue: CreateAppAttributes & EditAppAttributes): CreateAppAttributes {
        return {
            ownerId: formValue.ownerId,
            platform: formValue.platform,
            name: formValue.name,
            bundleId: formValue.bundleId,
            filterMatureContent: formValue.filterMatureContent,
            coppa: formValue.coppa,
            disableNetworksAutoconnection: formValue.disableNetworksAutoconnection,
        };
    }

    static getUpdateAppDto(formValue: CreateAppAttributes & EditAppAttributes): EditAppAttributes {
        return {
            name: formValue.name,
            bundleId: formValue.bundleId,
            filterMatureContent: formValue.filterMatureContent,
            coppa: formValue.coppa,
            enableStackCmp: formValue.enableStackCmp,
            interstitialMode: formValue.interstitialMode,
            s2sRewardCallbackUrl: formValue.s2sRewardCallbackUrl,
            s2sRewardCallbackKey: formValue.s2sRewardCallbackKey,
            googlePlayConsoleCredentials: formValue.googlePlayConsoleCredentials,
            testMode: formValue.testMode,
            disableAdmobSync: formValue.disableAdmobSync,
            testDeviceIds: [...new Set(formValue.testDeviceIds)],
            revenueShares: formValue.revenueShares.map(({email, percent}) => ({
                email,
                percent,
            })),
            unifiedApp: formValue.unifiedApp,
        };
    }

    static getUpdateAppAttributionSettingsDto(formValue: AppAttributionSettings): AppAttributionSettings {
        return {
            enableStackAttribution: formValue.enableStackAttribution,
            primaryMmpAccount: formValue.primaryMmpAccount,
            secondaryMmpAccount: formValue.secondaryMmpAccount,
            getSpendFromSecondaryMmpAccount: !!formValue.secondaryMmpAccount,
            primaryMmpRawDataSource: formValue.primaryMmpRawDataSource,
            secondaryMmpRawDataSource: formValue.secondaryMmpRawDataSource,
            attributionPlatform: formValue.attributionPlatform,
            adjustAppToken: formValue.adjustAppToken,
            adjustS2sToken: formValue.adjustS2sToken,
            adjustEnvironment: formValue.adjustEnvironment,
            appsflyerDevKey: formValue.appsflyerDevKey,
            appsflyerAppId: formValue.appsflyerAppId,
            firebaseConfigKeys: [...new Set(formValue.firebaseConfigKeys)],
            firebaseExpirationDuration: formValue.firebaseExpirationDuration,
            firebaseTracking: formValue.firebaseTracking,
            firebaseAdRevenueKey: formValue.firebaseAdRevenueKey,
            facebookTracking: formValue.facebookTracking,
            sendAfAdRevenue: formValue.sendAfAdRevenue,
            sendAfAdRevenueAttribution: formValue.sendAfAdRevenueAttribution,
            unityS2sIntegration: formValue.unityS2sIntegration,
            iapRevenueAppStoreFeePercent: formValue.iapRevenueAppStoreFeePercent,
            iapCustomEvents: formValue.iapCustomEvents,
        };
    }

    constructor(appModel: Partial<AppModel>) {
        Object.assign(this, appModel);
    }

    get isNew() {
        return !this.hasOwnProperty('id');
    }
}
